<template>
  <div class="seqSummary" :class="{ compact }">
    <!-- Step icon boxes -->
    <div
      v-for="step in steps"
      :key="step.step_index"
      class="stepSlot"
    >
      <!-- Left badge slot: in normal flow, drives slot width outward to the left.
           Negative margin-right pulls the iconBox over it to create the corner overlap look. -->
      <div
        v-if="stepCount(step.step_index) > 0"
        class="badgeSlot badgeSlot-left"
      >
        <span
          class="countBadge"
          :title="`${stepCount(step.step_index)} contact(s) awaiting action`"
        >{{ fmtBadge(stepCount(step.step_index)) }}</span>
      </div>

      <div
        class="iconBox"
        :class="[
          `iconBox-${step.step_type}`,
          {
            clickable,
            active: clickable && activeStepIndex === step.step_index && activeExitType === null,
          }
        ]"
        :title="clickable ? `Click to filter: ${stepLabel(step)}` : stepLabel(step)"
        :role="clickable ? 'button' : undefined"
        :tabindex="clickable ? 0 : undefined"
        @click="clickable ? emit('step-click', step.step_index) : undefined"
        @keydown.enter.prevent="clickable ? emit('step-click', step.step_index) : undefined"
      >
        <AppIcon v-if="step.step_type === 'email'" name="email" :size="iconSize" />
        <AppIcon v-else-if="step.step_type === 'voicemail'" name="voicemail" :size="iconSize" />
        <img
          v-else-if="step.step_type === 'linkedin_connect'"
          :src="linkedinFavicon"
          :width="iconSize"
          :height="iconSize"
          alt="LinkedIn"
          style="object-fit: contain; border-radius: 2px;"
        />
        <span v-else-if="step.step_type === 'wait'" class="waitDayText">{{ step.wait_days }}{{ step.wait_unit === 'minutes' ? 'm' : 'd' }}</span>
      </div>

      <!-- Right badge slot: mirrors left, drives slot width outward to the right. -->
      <div
        v-if="stepReadyCount(step.step_index) > 0"
        class="badgeSlot badgeSlot-right"
      >
        <span
          class="countBadge countBadge-ready"
          :title="`${stepReadyCount(step.step_index)} contact(s) ready to advance`"
        >{{ fmtBadge(stepReadyCount(step.step_index)) }}</span>
      </div>
    </div>

    <!-- Divider -->
    <div v-if="steps.length > 0" class="divider" />

    <!-- Awaiting Exit: virtual step for needs_exit_decision enrollments -->
    <div class="stepSlot">
      <div
        class="iconBox iconBox-exit iconBox-pending-exit"
        :class="{ clickable, active: clickable && activeExitType === 'pending_exit' }"
        :title="clickable ? 'Click to filter: Awaiting Exit' : `${pendingExit} awaiting exit decision`"
        :role="clickable ? 'button' : undefined"
        :tabindex="clickable ? 0 : undefined"
        @click="clickable ? emit('exit-click', 'pending_exit') : undefined"
        @keydown.enter.prevent="clickable ? emit('exit-click', 'pending_exit') : undefined"
      >
        <AppIcon name="signal" :size="iconSize" />
      </div>
      <div v-if="pendingExit > 0" class="badgeSlot badgeSlot-right">
        <span class="countBadge countBadge-pending-exit">{{ fmtBadge(pendingExit) }}</span>
      </div>
    </div>

    <!-- Exit: engaged -->
    <div class="stepSlot">
      <div
        class="iconBox iconBox-exit iconBox-completed"
        :class="{ clickable, active: clickable && activeExitType === 'completed' }"
        :title="clickable ? 'Click to filter: Engaged' : `${exitedCompleted} engaged`"
        :role="clickable ? 'button' : undefined"
        :tabindex="clickable ? 0 : undefined"
        @click="clickable ? emit('exit-click', 'completed') : undefined"
        @keydown.enter.prevent="clickable ? emit('exit-click', 'completed') : undefined"
      >
        <AppIcon name="task" :size="iconSize" />
      </div>
      <div v-if="exitedCompleted > 0" class="badgeSlot badgeSlot-right">
        <span class="countBadge countBadge-completed">{{ fmtBadge(exitedCompleted) }}</span>
      </div>
    </div>

    <!-- Exit: timed out -->
    <div class="stepSlot">
      <div
        class="iconBox iconBox-exit iconBox-timed-out"
        :class="{ clickable, active: clickable && activeExitType === 'timed_out' }"
        :title="clickable ? 'Click to filter: Timed Out' : `${exitedTimedOut} timed out`"
        :role="clickable ? 'button' : undefined"
        :tabindex="clickable ? 0 : undefined"
        @click="clickable ? emit('exit-click', 'timed_out') : undefined"
        @keydown.enter.prevent="clickable ? emit('exit-click', 'timed_out') : undefined"
      >
        <AppIcon name="followup" :size="iconSize" />
      </div>
      <div v-if="exitedTimedOut > 0" class="badgeSlot badgeSlot-right">
        <span class="countBadge countBadge-timed-out">{{ fmtBadge(exitedTimedOut) }}</span>
      </div>
    </div>

    <!-- Exit: removed -->
    <div class="stepSlot">
      <div
        class="iconBox iconBox-exit iconBox-removed"
        :class="{ clickable, active: clickable && activeExitType === 'removed' }"
        :title="clickable ? 'Click to filter: Disqualified/Removed' : `${exitedDisqualified} disqualified`"
        :role="clickable ? 'button' : undefined"
        :tabindex="clickable ? 0 : undefined"
        @click="clickable ? emit('exit-click', 'removed') : undefined"
        @keydown.enter.prevent="clickable ? emit('exit-click', 'removed') : undefined"
      >
        <AppIcon name="close" :size="iconSize" />
      </div>
      <div v-if="exitedDisqualified > 0" class="badgeSlot badgeSlot-right">
        <span class="countBadge countBadge-removed">{{ fmtBadge(exitedDisqualified) }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import AppIcon from './AppIcon.vue'
import { googleFaviconUrl } from '../services/favicons.ts'

const props = defineProps({
  steps: { type: Array, default: () => [] },
  stepCounts: { type: Array, default: () => [] },
  stepCountsReady: { type: Array, default: () => [] },
  pendingExit: { type: Number, default: 0 },
  exitedCompleted: { type: Number, default: 0 },
  exitedTimedOut: { type: Number, default: 0 },
  exitedDisqualified: { type: Number, default: 0 },
  compact: { type: Boolean, default: false },
  clickable: { type: Boolean, default: false },
  activeStepIndex: { type: Number, default: null },
  activeExitType: { type: String, default: null },
})

const emit = defineEmits(['step-click', 'exit-click'])

const iconSize = computed(() => props.compact ? 14 : 18)

const linkedinFavicon = googleFaviconUrl('linkedin.com', { size: 32 })

function stepCount(stepIndex) {
  return props.stepCounts[stepIndex] ?? 0
}

function stepReadyCount(stepIndex) {
  return props.stepCountsReady[stepIndex] ?? 0
}

function fmtBadge(n) {
  if (n >= 10000) return Math.round(n / 1000) + 'k'
  if (n >= 1000) return (Math.round(n / 100) / 10) + 'k'
  return String(n)
}

function stepLabel(step) {
  if (step.step_type === 'wait') {
    const unit = step.wait_unit === 'minutes' ? (step.wait_days !== 1 ? 'minutes' : 'minute') : (step.wait_days !== 1 ? 'days' : 'day')
    return `Wait ${step.wait_days} ${unit}`
  }
  const labels = { email: 'Email', linkedin_connect: 'LinkedIn Connect', voicemail: 'Voicemail' }
  const label = labels[step.step_type] || step.step_type
  return step.brief ? `${label}: ${step.brief}` : label
}
</script>

<style scoped>
.seqSummary {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.seqSummary.compact {
  gap: 4px;
}

/* Outer slot: flex row with [leftBadgeSlot?][iconBox][rightBadgeSlot?].
   Badge slots are in normal flow so their widths push the slot boundaries outward,
   preventing overlap with adjacent slots. The icon box stays fixed-size.
   padding-bottom makes room for badges to hang below the icon bottom edge. */
.stepSlot {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  flex-shrink: 0;
  padding-bottom: 5px;
}

.compact .stepSlot {
  padding-bottom: 4px;
}

/* Badge slots: in normal flow, width = badge content width.
   Negative outer margins pull the iconBox over the slot (or vice versa),
   creating the visual corner-overlap effect.
   margin-bottom: -5px drops the badge 5px below the icon box bottom,
   matching the original `bottom: -5px` absolute positioning. */
.badgeSlot {
  display: flex;
  align-items: flex-end;
  align-self: flex-end;
  margin-bottom: -5px;
  position: relative;
  z-index: 1;
}

/* Left slot: badge right-aligned, negative margin-right overlaps iconBox left corner.
   The -13px value is chosen so that a single-digit badge (~18px wide) sits at
   exactly the same position as the original `left: -5px` absolute placement. */
.badgeSlot-left {
  justify-content: flex-end;
  margin-right: -13px;
}

/* Right slot: badge left-aligned, negative margin-left overlaps iconBox right corner. */
.badgeSlot-right {
  justify-content: flex-start;
  margin-left: -13px;
}

.compact .badgeSlot {
  margin-bottom: -4px;
}

.compact .badgeSlot-left {
  margin-right: -10px;
}

.compact .badgeSlot-right {
  margin-left: -10px;
}

.iconBox {
  width: 34px;
  height: 34px;
  border: 1px solid var(--border);
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: var(--surface);
  flex-shrink: 0;
  color: var(--muted);
  transition: border-color 0.12s, box-shadow 0.12s, background 0.12s;
  position: relative;
  z-index: 0;
}

.compact .iconBox {
  width: 28px;
  height: 28px;
  border-radius: 8px;
}

.iconBox.clickable {
  cursor: pointer;
}

.iconBox.clickable:hover {
  border-color: currentColor;
  background: color-mix(in srgb, currentColor 8%, var(--surface));
}

.iconBox.clickable.active {
  border-color: currentColor;
  border-width: 2px;
  background: color-mix(in srgb, currentColor 12%, var(--surface));
  box-shadow: 0 0 0 2px color-mix(in srgb, currentColor 20%, transparent);
}

.iconBox.clickable:focus-visible {
  outline: 2px solid var(--focus-border);
  outline-offset: 2px;
}

/* Step type colors */
.iconBox-email { color: var(--primary); }
.iconBox-linkedin_connect { color: #0A66C2; }
.iconBox-voicemail { color: #8b5cf6; }
.iconBox-wait { color: var(--muted); }

/* Exit condition boxes: dotted border */
.iconBox-exit {
  border-style: dashed;
}

.iconBox-completed { color: var(--success); }
.iconBox-timed-out { color: var(--muted); }
.iconBox-removed { color: var(--danger); }
.iconBox-pending-exit { color: var(--warning); }

.waitDayText {
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.compact .waitDayText {
  font-size: 9px;
}

/* Count badge */
.countBadge {
  min-width: 18px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--primary);
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  box-shadow: 0 0 0 2px var(--surface);
  white-space: nowrap;
  pointer-events: none;
}

.compact .countBadge {
  min-width: 14px;
  height: 14px;
  font-size: 8px;
}

.countBadge-ready { background: var(--muted); }
.countBadge-pending-exit { background: var(--warning); }
.countBadge-completed { background: var(--success); }
.countBadge-timed-out { background: var(--muted); }
.countBadge-removed { background: var(--danger); }

.divider {
  width: 1px;
  height: 34px;
  background: var(--border);
  flex-shrink: 0;
  margin: 0 2px;
}

.compact .divider {
  height: 28px;
}
</style>
